import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';

import { CommonModule, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  Attachment,
  AttachmentControllerService,
} from '../../core/generated';

import { DialogUploadComponent } from './dialog-upload/dialog-upload.component';
import { TruncatePipe } from '../truncate.pipe';
import { firstValueFrom } from 'rxjs';
import { DialogConfirmComponent } from './dialog-confirm.component';

@Component({
    selector: 'app-attachment',
    standalone: true,
    imports: [
      CommonModule,
      MatButtonModule,
      MatIconModule,
      MatTooltipModule,
      MatMenuModule,
      MatDialogModule,
      TruncatePipe
    ],
    templateUrl: './attachment.component.html',
    styleUrl: './attachment.component.scss'
})
export class AttachmentComponent implements OnInit {
  public dialog = inject(MatDialog);
  private attachmentsService = inject(AttachmentControllerService);
  private cd = inject(ChangeDetectorRef);
  private snackBar = inject(MatSnackBar);

  @Input()
  documentTypesRequired!: string[];
  @Input()
  ownerDocumentsCpfCnpj!: string;
  @Input()
  typeAccess: 'admin' | 'owner' | 'granted' = 'owner';
  @Input()
  attachments: Attachment[] = [];
  @Output()
  attachmentChanged: EventEmitter<void> = new EventEmitter();

  isWaiting = false;

  ngOnInit(): void {
    this.getAttachments(this.ownerDocumentsCpfCnpj);
  }

  addAttachment(documentType?: string) {
    const dialogRef = this.dialog.open(DialogUploadComponent, {
      data: {
        documentType: documentType,
        ownerDocumentsCpfCnpj: this.ownerDocumentsCpfCnpj,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.attachments = result;
        this.attachmentChanged.emit();
      }
    });
    this.cd.detectChanges();
  }

  async removeFile(attachmentId: number | undefined) {
    if (!attachmentId) return;
    const dialogRef = this.dialog.open(DialogConfirmComponent);
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (confirmed) {
      this.attachmentsService.deleteFile(attachmentId).subscribe((response: { [key: string]: boolean }) => {
        if (response) {
          this.attachments = this.attachments.filter(
            (attach) => attach.id != attachmentId
          );
          this.attachmentChanged.emit();
        }
      });
    }
  }

  async getAttachments(ownerDocumentsCpfCnpj: string) {
    this.isWaiting = true;
    this.attachments = await firstValueFrom(
      this.attachmentsService.getFilesListByOwner(this.ownerDocumentsCpfCnpj)
    );
    this.isWaiting = false;

    this.cd.detectChanges();
  }

  download(fileName: string | undefined) {
    if (fileName) {
      this.attachmentsService.downloadFile(fileName).subscribe((file) => {
        // Create a URL for the blob directly
        const url = URL.createObjectURL(file);

        // Create an anchor element and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    }
  }

  alreadyAttachedCheckMark(documentType: string) {
    return this.attachments?.some((doc) => doc.documentType === documentType) ?? false;
  }

  getFiles(docType: string): Attachment[] {
    return this.attachments?.filter((a) => a.documentType === docType) || [];
  }

  getExtraFiles(): Attachment[] {
    if (!this.attachments) return [];
    return this.attachments.filter(
      (a) => !this.documentTypesRequired?.includes(a.documentType || '')
    );
  }

  shareFile(attachment: Attachment) {
    if (!attachment.id) {
      console.error('Attachment ID is missing');
      this.snackBar.open('Erro: ID do anexo não encontrado', 'Fechar', { duration: 3000 });
      return;
    }

    const newSharePermittedValue = !attachment.sharePermitted;
    
    this.attachmentsService
      .putMethodName(attachment.id, newSharePermittedValue)
      .subscribe({
        next: (response: { [key: string]: boolean }) => {
          console.log('Share file response:', response);
          // Backend retorna { "sharePermitted": true/false }
          if (response && response['sharePermitted'] !== undefined) {
            const file = this.attachments.find((f) => f.id === attachment.id);
            if (file) {
              file.sharePermitted = response['sharePermitted'];
              this.cd.detectChanges();
            }
            this.attachmentChanged.emit();
            
            const message = response['sharePermitted'] 
              ? 'Compartilhamento permitido com sucesso!' 
              : 'Compartilhamento bloqueado com sucesso!';
            this.snackBar.open(message, 'Fechar', { duration: 3000 });
          } else {
            console.error('Failed to update share permission: invalid response format');
            this.snackBar.open('Erro ao atualizar permissão de compartilhamento', 'Fechar', { duration: 3000 });
          }
        },
        error: (error) => {
          console.error('Error updating share permission:', error);
          this.snackBar.open('Erro ao atualizar permissão de compartilhamento', 'Fechar', { duration: 3000 });
        }
      });
  }
}
